// v84.157 — LLM Provider settings (Google Gemini / OpenRouter)
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Zap, CheckCircle2, XCircle, Save, RefreshCw, Send, MessageCircle, Rocket } from "lucide-react";
import { http, api } from "@/lib/api";

const PROVIDERS = [
  { id: "google", label: "Google AI Studio (Gemini)" },
  { id: "openrouter", label: "OpenRouter" },
];

export default function LLMSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  const [provider, setProvider] = useState("google");
  const [storedProvider, setStoredProvider] = useState("google");
  const [apiKey, setApiKey] = useState("");
  const [apiKeyMasked, setApiKeyMasked] = useState("");
  const [apiKeySet, setApiKeySet] = useState(false);
  const [extractorModel, setExtractorModel] = useState("");
  const [executorModel, setExecutorModel] = useState("");
  const [adminModel, setAdminModel] = useState("");
  const [arrangerModel, setArrangerModel] = useState("");
  const [models, setModels] = useState([]);
  const [modelsError, setModelsError] = useState("");
  const [testResult, setTestResult] = useState(null);   // {ok, sample, error}

  // v84.170 — Test Webview WhatsApp state
  const DEFAULT_WEBVIEW_BODY =
    "Halo Kak 👋 Untuk order atau cek jadwal servis AC, Kakak bisa langsung pilih jadwal di sini tanpa perlu menunggu admin.";
  const DEFAULT_WEBVIEW_URL = "https://dispatcher.globaltehnik.com/quick-order";
  const [wvPhone, setWvPhone] = useState("");
  const [wvBody, setWvBody] = useState(DEFAULT_WEBVIEW_BODY);
  const [wvUrl, setWvUrl] = useState(DEFAULT_WEBVIEW_URL);
  const [wvCta, setWvCta] = useState("Pilih Jadwal");
  const [wvSending, setWvSending] = useState(false);
  const [wvResult, setWvResult] = useState(null);   // {ok, simulated, wamid, message, error}

  // v84.176 — Force Quick Order Mode state
  const [fqoEnabled, setFqoEnabled] = useState(false);
  const [fqoLoading, setFqoLoading] = useState(true);
  const [fqoSaving, setFqoSaving] = useState(false);
  const [fqoMeta, setFqoMeta] = useState({ updated_at: null, updated_by: null });

  // v84.182 — Editable body text
  const [fqoBody, setFqoBody] = useState("");
  const [fqoBodyDefault, setFqoBodyDefault] = useState("");
  const [fqoBodyMax, setFqoBodyMax] = useState(1024);
  const [fqoBodySavedText, setFqoBodySavedText] = useState("");
  const [fqoBodySaving, setFqoBodySaving] = useState(false);

  // v84.184 — CTA text + exclusion patterns
  const [fqoCta, setFqoCta] = useState("");
  const [fqoCtaSaved, setFqoCtaSaved] = useState("");
  const [fqoCtaDefault, setFqoCtaDefault] = useState("Pilih Jadwal");
  const [fqoCtaSaving, setFqoCtaSaving] = useState(false);
  const [fqoThanks, setFqoThanks] = useState("");         // comma-separated
  const [fqoThanksSaved, setFqoThanksSaved] = useState("");
  const [fqoThanksDefault, setFqoThanksDefault] = useState("");
  const [fqoCasual, setFqoCasual] = useState("");        // comma-separated
  const [fqoCasualSaved, setFqoCasualSaved] = useState("");
  const [fqoCasualDefault, setFqoCasualDefault] = useState("");
  const [fqoPatSaving, setFqoPatSaving] = useState(false);

  // v84.180 — Force QO diagnostic test-send state
  const [fqoDiagPhone, setFqoDiagPhone] = useState("");
  const [fqoDiagToken, setFqoDiagToken] = useState("");
  const [fqoDiagSending, setFqoDiagSending] = useState(false);
  const [fqoDiagResult, setFqoDiagResult] = useState(null);   // full response object

  // Load existing settings
  useEffect(() => {
    http.get("/admin/llm-settings").then(r => {
      setProvider(r.data.provider || "google");
      setStoredProvider(r.data.provider || "google");
      setApiKeyMasked(r.data.api_key_masked || "");
      setApiKeySet(!!r.data.api_key_set);
      setExtractorModel(r.data.extractor_model || "");
      setExecutorModel(r.data.executor_model || "");
      setAdminModel(r.data.admin_model || "");
      setArrangerModel(r.data.arranger_model || "");
    }).catch(e => toast.error("Gagal load settings: " + (e?.response?.data?.detail || "")))
      .finally(() => setLoading(false));
  }, []);

  // v84.176 — Load Force Quick Order status
  useEffect(() => {
    api.getForceQuickOrder()
      .then(r => {
        setFqoEnabled(!!r.enabled);
        setFqoMeta({ updated_at: r.updated_at, updated_by: r.updated_by });
        // v84.182 — populate body editor
        setFqoBody(r.body_text || "");
        setFqoBodySavedText(r.body_text || "");
        setFqoBodyDefault(r.body_text_default || "");
        setFqoBodyMax(r.body_text_max_chars || 1024);
        // v84.184 — CTA + exclusion patterns
        setFqoCta(r.cta_text || "");
        setFqoCtaSaved(r.cta_text || "");
        setFqoCtaDefault(r.cta_text_default || "Pilih Jadwal");
        const thanksStr = (r.thanks_patterns || []).join(", ");
        const casualStr = (r.reminder_casual_replies || []).join(", ");
        setFqoThanks(thanksStr);
        setFqoThanksSaved(thanksStr);
        setFqoThanksDefault((r.thanks_patterns_default || []).join(", "));
        setFqoCasual(casualStr);
        setFqoCasualSaved(casualStr);
        setFqoCasualDefault((r.reminder_casual_replies_default || []).join(", "));
      })
      .catch(e => toast.error("Gagal load Force QO: " + (e?.response?.data?.detail || String(e))))
      .finally(() => setFqoLoading(false));
  }, []);

  const toggleFqo = async () => {
    const next = !fqoEnabled;
    setFqoSaving(true);
    try {
      await api.setForceQuickOrder(next);
      setFqoEnabled(next);
      setFqoMeta({ updated_at: new Date().toISOString(), updated_by: "you" });
      toast.success(next
        ? "Force Quick Order Mode: ON — template terkirim 1× per customer / 72h"
        : "Force Quick Order Mode: OFF — kembali ke perilaku AI normal");
    } catch (e) {
      toast.error("Gagal toggle: " + (e?.response?.data?.detail || String(e)));
    } finally {
      setFqoSaving(false);
    }
  };

  // v84.182 — Save body text (kosong = reset default)
  const saveFqoBody = async () => {
    setFqoBodySaving(true);
    try {
      const r = await api.updateForceQuickOrderBody(fqoBody.trim());
      setFqoBody(r.body_text || "");
      setFqoBodySavedText(r.body_text || "");
      setFqoMeta({ updated_at: new Date().toISOString(), updated_by: "you" });
      toast.success(fqoBody.trim() ? "Isi pesan Force QO tersimpan" : "Isi pesan direset ke default");
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || String(e)));
    } finally {
      setFqoBodySaving(false);
    }
  };

  // v84.184 — Save CTA text (kosong = reset default)
  const saveFqoCta = async () => {
    setFqoCtaSaving(true);
    try {
      const r = await api.updateForceQuickOrderCta(fqoCta.trim());
      setFqoCta(r.cta_text || "");
      setFqoCtaSaved(r.cta_text || "");
      toast.success(fqoCta.trim() ? `Teks tombol tersimpan: "${r.cta_text}"` : "Teks tombol direset ke default");
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || String(e)));
    } finally {
      setFqoCtaSaving(false);
    }
  };

  // v84.184 — Save exclusion patterns (both thanks & reminder casual)
  const saveFqoPatterns = async () => {
    setFqoPatSaving(true);
    try {
      // Parse comma-separated → List[str]. Backend cleans further.
      const thanksList = fqoThanks.split(",").map(s => s.trim()).filter(Boolean);
      const casualList = fqoCasual.split(",").map(s => s.trim()).filter(Boolean);
      const r = await api.updateForceQuickOrderPatterns(thanksList, casualList);
      const thanksStr = (r.thanks_patterns || []).join(", ");
      const casualStr = (r.reminder_casual_replies || []).join(", ");
      setFqoThanks(thanksStr); setFqoThanksSaved(thanksStr);
      setFqoCasual(casualStr); setFqoCasualSaved(casualStr);
      toast.success("Pola exclusion tersimpan");
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || String(e)));
    } finally {
      setFqoPatSaving(false);
    }
  };

  // v84.180 — Test-send Force QO template ke nomor tujuan
  const sendFqoDiag = async () => {
    if (!fqoDiagPhone.trim()) {
      toast.error("Isi nomor tujuan dulu");
      return;
    }
    setFqoDiagSending(true);
    setFqoDiagResult(null);
    try {
      const r = await api.testForceQuickOrderSend(
        fqoDiagPhone.trim(),
        fqoDiagToken.trim() || null,
      );
      setFqoDiagResult(r);
      if (r.ok) toast.success(`Template dikirim ke ${r.phone_norm}. Cek WA & buka CTA.`);
      else toast.error("Gagal kirim template. Cek detail di bawah.");
    } catch (e) {
      const err = e?.response?.data?.detail || String(e);
      setFqoDiagResult({ ok: false, error: err });
      toast.error("Error: " + err);
    } finally {
      setFqoDiagSending(false);
    }
  };

  // Fetch model list from provider
  const fetchModels = useCallback(async () => {
    if (!apiKey && !apiKeySet) {
      toast.error("Isi API Key dulu");
      return;
    }
    setLoadingModels(true);
    setModelsError("");
    try {
      const params = new URLSearchParams({provider});
      if (apiKey) params.set("api_key", apiKey);
      const r = await http.get(`/admin/llm-settings/models?${params}`);
      if (r.data.ok) {
        setModels(r.data.models || []);
        if (!(r.data.models || []).length) setModelsError("Daftar model kosong");
      } else {
        setModelsError(r.data.error || "Gagal ambil model");
        setModels([]);
      }
    } catch (e) {
      setModelsError(e?.response?.data?.detail || String(e));
      setModels([]);
    } finally {
      setLoadingModels(false);
    }
  }, [provider, apiKey, apiKeySet]);

  const save = async () => {
    setSaving(true);
    try {
      await http.post("/admin/llm-settings", {
        provider, api_key: apiKey || "",
        extractor_model: extractorModel, executor_model: executorModel,
        admin_model: adminModel,
        arranger_model: arrangerModel,
      });
      toast.success("Settings disimpan. Perubahan langsung berlaku.");
      // Reload masked key
      const r = await http.get("/admin/llm-settings");
      setApiKey("");
      setApiKeyMasked(r.data.api_key_masked || "");
      setApiKeySet(!!r.data.api_key_set);
      setStoredProvider(r.data.provider || "google");
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || String(e)));
    } finally { setSaving(false); }
  };

  const testConn = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await http.post("/admin/llm-settings/test", {
        provider, api_key: apiKey || "",
        extractor_model: extractorModel, executor_model: executorModel,
      });
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Berhasil: "${(r.data.sample || "").slice(0, 60)}"`);
      else toast.error("Gagal: " + (r.data.error || ""));
    } catch (e) {
      const err = e?.response?.data?.detail || String(e);
      setTestResult({ok: false, error: err});
      toast.error("Test gagal: " + err);
    } finally { setTesting(false); }
  };

  // v84.170 — Kirim test webview WhatsApp
  const sendWebviewTest = async () => {
    if (!wvPhone.trim()) {
      toast.error("Isi nomor tujuan dulu (contoh: 08123456789)");
      return;
    }
    setWvSending(true);
    setWvResult(null);
    try {
      const r = await api.testWebviewWhatsapp({
        phone: wvPhone.trim(),
        body: wvBody || undefined,
        url: wvUrl || undefined,
        cta_text: wvCta || undefined,
      });
      setWvResult(r);
      if (r.simulated) toast.success("Simulasi OK — mode simulator (tidak dikirim ke Meta)");
      else toast.success(`Terkirim ke ${r.to}${r.wamid ? " (wamid: " + r.wamid.slice(0, 12) + "…)" : ""}`);
    } catch (e) {
      const err = e?.response?.data?.detail || String(e);
      setWvResult({ ok: false, error: err });
      toast.error("Gagal kirim: " + err);
    } finally {
      setWvSending(false);
    }
  };

  if (loading) return <div className="p-8"><Loader2 className="animate-spin inline mr-2"/>Loading…</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5" data-testid="llm-settings-page">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-6 h-6 text-orange-500" />
        <h1 className="text-xl font-bold">Pengaturan LLM (AI Provider)</h1>
      </div>
      <div className="text-[12px] text-zinc-500 border-l-2 border-orange-400 pl-3 py-1 leading-snug">
        Perubahan langsung berlaku tanpa redeploy. LLM AI CS WhatsApp + Extractor + AI Admin
        akan pakai settings di sini. Provider bisa Google AI Studio (Gemini) atau OpenRouter
        (akses Claude, GPT, dll).
      </div>

      {/* v84.176 — Force Quick Order Mode */}
      <div
        className={`rounded-lg border-2 p-4 ${fqoEnabled ? "border-emerald-400 bg-emerald-50" : "border-zinc-200 bg-zinc-50"}`}
        data-testid="force-qo-section"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Rocket className={`w-5 h-5 ${fqoEnabled ? "text-emerald-600" : "text-zinc-400"}`} />
              <h2 className="text-base font-bold">Force Quick Order Mode</h2>
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${fqoEnabled ? "bg-emerald-600 text-white" : "bg-zinc-300 text-zinc-700"}`}
                data-testid="force-qo-status-badge"
              >
                {fqoEnabled ? "ON" : "OFF"}
              </span>
            </div>
            <p className="text-[12px] text-zinc-600 leading-snug">
              Saat <b>ON</b>: setiap pesan masuk customer di WA CS akan otomatis
              dibalas <b>Interactive CTA URL Button</b> dgn link <b>personal per customer</b>{" "}
              <b>1× per customer / 72 jam</b>. AI bot di-pause utk customer
              tsb selama 72 jam. Admin manual tetap bisa balas. New customer
              (belum ada alamat) dikecualikan — AI/admin balas normal.
              <br/>
              Saat <b>OFF</b>: sistem kembali normal, AI bot handle chat seperti biasa.
            </p>
            {!fqoLoading && fqoMeta.updated_at && (
              <div className="text-[10px] text-zinc-400 mt-2">
                Last update: {new Date(fqoMeta.updated_at).toLocaleString("id-ID")}
                {fqoMeta.updated_by && ` · by ${fqoMeta.updated_by}`}
              </div>
            )}
          </div>
          <button
            onClick={toggleFqo}
            disabled={fqoLoading || fqoSaving}
            role="switch"
            aria-checked={fqoEnabled}
            className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${fqoEnabled ? "bg-emerald-600" : "bg-zinc-300"}`}
            data-testid="force-qo-toggle"
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${fqoEnabled ? "translate-x-7" : "translate-x-1"}`}
            />
            {fqoSaving && <Loader2 className="absolute inset-0 m-auto w-3.5 h-3.5 animate-spin text-white"/>}
          </button>
        </div>

        {/* v84.182 — Editable Body Text */}
        <div className="mt-4 pt-3 border-t border-dashed border-zinc-300" data-testid="force-qo-body-section">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-bold text-zinc-700 uppercase tracking-wider">
              ✏️ Isi Pesan (Body Interactive CTA)
            </div>
            <div className="text-[10px] text-zinc-500">
              {fqoBody.length} / {fqoBodyMax}
            </div>
          </div>
          <textarea
            rows={3}
            value={fqoBody}
            maxLength={fqoBodyMax}
            onChange={e => setFqoBody(e.target.value)}
            placeholder={fqoBodyDefault}
            className="w-full rounded-md border px-3 py-2 text-[13px] leading-snug bg-white resize-y"
            data-testid="force-qo-body-input"
          />
          <div className="flex flex-wrap gap-2 mt-2">
            <button
              onClick={saveFqoBody}
              disabled={fqoBodySaving || fqoBody === fqoBodySavedText}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold disabled:opacity-40"
              data-testid="force-qo-body-save"
            >
              {fqoBodySaving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
              Simpan Isi Pesan
            </button>
            <button
              onClick={() => setFqoBody(fqoBodyDefault)}
              disabled={fqoBody === fqoBodyDefault}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-100 hover:bg-zinc-200 border text-[12px] font-semibold disabled:opacity-40"
              data-testid="force-qo-body-reset"
            >
              <RefreshCw className="w-3.5 h-3.5"/> Reset ke default
            </button>
            {fqoBody !== fqoBodySavedText && (
              <span className="inline-flex items-center px-2 py-1 rounded bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-bold">
                Perubahan belum tersimpan
              </span>
            )}
          </div>
          <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
            Tips: gunakan emoji + panggilan personal (Kak, Bapak/Ibu) supaya lebih hangat.
            Max {fqoBodyMax} karakter. Kosongkan lalu simpan untuk revert ke default.
          </p>
        </div>

        {/* v84.184 — Editable CTA Text */}
        <div className="mt-4 pt-3 border-t border-dashed border-zinc-300" data-testid="force-qo-cta-section">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-bold text-zinc-700 uppercase tracking-wider">
              🎯 Teks Tombol CTA
            </div>
            <div className="text-[10px] text-zinc-500">
              {fqoCta.length} / 20
            </div>
          </div>
          <div className="grid grid-cols-1 [@media(min-width:600px)]:grid-cols-[1fr_auto_auto] gap-2 items-start">
            <input
              type="text"
              value={fqoCta}
              maxLength={20}
              onChange={e => setFqoCta(e.target.value)}
              placeholder={fqoCtaDefault}
              className="rounded-md border px-3 py-2 text-sm bg-white"
              data-testid="force-qo-cta-input"
            />
            <button
              onClick={saveFqoCta}
              disabled={fqoCtaSaving || fqoCta === fqoCtaSaved}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold disabled:opacity-40"
              data-testid="force-qo-cta-save"
            >
              {fqoCtaSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
              Simpan
            </button>
            <button
              onClick={() => setFqoCta(fqoCtaDefault)}
              disabled={fqoCta === fqoCtaDefault}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-100 hover:bg-zinc-200 border text-[12px] font-semibold disabled:opacity-40"
              data-testid="force-qo-cta-reset"
            >
              <RefreshCw className="w-3.5 h-3.5"/> Reset
            </button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
            Teks pada tombol CTA di WA (max 20 karakter, limit Meta WA Cloud API).
            Default: <code className="bg-white px-1 rounded border">{fqoCtaDefault}</code>
          </p>
        </div>

        {/* v84.184 — Editable Exclusion Patterns */}
        <div className="mt-4 pt-3 border-t border-dashed border-zinc-300" data-testid="force-qo-patterns-section">
          <div className="text-[11px] font-bold text-zinc-700 mb-2 uppercase tracking-wider">
            🚫 Pola Pengecualian (Exclusion Patterns)
          </div>
          <p className="text-[11px] text-zinc-600 mb-3 leading-snug">
            Pesan customer yg cocok pola berikut TIDAK akan dibalas Force QO —
            AI/admin balas normal. Format: <b>pisah dengan koma</b>. Case-insensitive.
          </p>

          <div className="mb-3">
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
              Frase Terimakasih ({fqoThanks.split(",").filter(s => s.trim()).length} pola)
            </label>
            <textarea
              rows={2}
              value={fqoThanks}
              onChange={e => setFqoThanks(e.target.value)}
              placeholder={fqoThanksDefault}
              className="w-full rounded-md border px-3 py-2 text-[13px] font-mono bg-white resize-y"
              data-testid="force-qo-thanks-input"
            />
            <button
              onClick={() => setFqoThanks(fqoThanksDefault)}
              disabled={fqoThanks === fqoThanksDefault}
              className="mt-1 text-[10px] underline text-zinc-500 hover:text-zinc-800 disabled:opacity-30"
              data-testid="force-qo-thanks-reset-link"
            >
              Reset ke default
            </button>
          </div>

          <div className="mb-3">
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
              Casual Reply setelah Reminder ({fqoCasual.split(",").filter(s => s.trim()).length} pola)
            </label>
            <textarea
              rows={2}
              value={fqoCasual}
              onChange={e => setFqoCasual(e.target.value)}
              placeholder={fqoCasualDefault}
              className="w-full rounded-md border px-3 py-2 text-[13px] font-mono bg-white resize-y"
              data-testid="force-qo-casual-input"
            />
            <button
              onClick={() => setFqoCasual(fqoCasualDefault)}
              disabled={fqoCasual === fqoCasualDefault}
              className="mt-1 text-[10px] underline text-zinc-500 hover:text-zinc-800 disabled:opacity-30"
              data-testid="force-qo-casual-reset-link"
            >
              Reset ke default
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={saveFqoPatterns}
              disabled={fqoPatSaving ||
                (fqoThanks === fqoThanksSaved && fqoCasual === fqoCasualSaved)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold disabled:opacity-40"
              data-testid="force-qo-patterns-save"
            >
              {fqoPatSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
              Simpan Pola
            </button>
            {(fqoThanks !== fqoThanksSaved || fqoCasual !== fqoCasualSaved) && (
              <span className="inline-flex items-center px-2 py-1 rounded bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-bold">
                Perubahan belum tersimpan
              </span>
            )}
          </div>
          <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
            Contoh: <code className="bg-white px-1 rounded border">terimakasih, makasih, mksh</code>.
            Kosongkan textarea lalu simpan untuk revert ke default.
          </p>
        </div>

        {/* v84.180 — Diagnostic Test-Send */}
        <div className="mt-4 pt-3 border-t border-dashed border-zinc-300" data-testid="force-qo-diag-section">
          <div className="text-[11px] font-bold text-zinc-700 mb-2 uppercase tracking-wider">
            🔍 Diagnostic: Test-Send Interactive CTA
          </div>
          <p className="text-[11px] text-zinc-600 mb-2 leading-snug">
            Kirim <b>Interactive CTA URL Button</b> Force QO ke nomor tujuan
            dgn URL personal per customer. Butuh 24h WA session window terbuka
            (biasanya customer sudah pernah kirim pesan dalam 24 jam terakhir).
          </p>
          <div className="grid grid-cols-1 [@media(min-width:600px)]:grid-cols-[1fr_1fr_auto] gap-2 items-start">
            <input
              type="tel"
              value={fqoDiagPhone}
              onChange={e => setFqoDiagPhone(e.target.value)}
              placeholder="Nomor tujuan (08xxx)"
              className="rounded-md border px-3 py-2 text-sm font-mono bg-white"
              data-testid="force-qo-diag-phone"
            />
            <input
              type="text"
              value={fqoDiagToken}
              onChange={e => setFqoDiagToken(e.target.value)}
              placeholder="Token override (opsional, kosong = auto-generate)"
              className="rounded-md border px-3 py-2 text-sm font-mono bg-white"
              data-testid="force-qo-diag-token"
            />
            <button
              onClick={sendFqoDiag}
              disabled={fqoDiagSending || !fqoDiagPhone.trim()}
              className="inline-flex items-center gap-1.5 justify-center px-4 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="force-qo-diag-send"
            >
              {fqoDiagSending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
              Test-Send
            </button>
          </div>

          {fqoDiagResult && (
            <div
              className={`mt-3 rounded-md p-3 text-[12px] ${fqoDiagResult.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-red-50 border border-red-200 text-red-900"}`}
              data-testid="force-qo-diag-result"
            >
              <div className="font-bold mb-1">
                {fqoDiagResult.ok ? "✓ Terkirim" : "✗ Gagal"}
              </div>
              {fqoDiagResult.expected_url && (
                <div className="mb-2 break-all">
                  <b>Expected URL (setelah substitusi):</b><br/>
                  <code className="bg-white px-1 rounded border text-[11px]">{fqoDiagResult.expected_url}</code>
                </div>
              )}
              {fqoDiagResult.token_used && (
                <div className="mb-2">
                  <b>Token dipakai:</b>{" "}
                  <code className="bg-white px-1 rounded border text-[11px]">{fqoDiagResult.token_used}</code>
                  {" "}({fqoDiagResult.token_used.length} chars)
                </div>
              )}
              {fqoDiagResult.hint && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[11px] font-bold underline">Troubleshooting hint (klik)</summary>
                  <div className="mt-1 text-[11px] whitespace-pre-wrap">{fqoDiagResult.hint}</div>
                </details>
              )}
              <details className="mt-2">
                <summary className="cursor-pointer text-[11px] font-bold underline">Full Meta response (klik)</summary>
                <pre className="mt-1 text-[10px] bg-white p-2 rounded border overflow-auto max-h-64">
{JSON.stringify(fqoDiagResult.meta_response || fqoDiagResult, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      </div>

      {/* Provider */}
      <div>
        <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Provider</label>
        <select value={provider} onChange={e => { setProvider(e.target.value); setModels([]); }}
          className="w-full rounded-md border px-3 py-2 text-sm bg-white"
          data-testid="llm-provider-select">
          {PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </div>

      {/* API Key */}
      <div>
        <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
          API Key {apiKeySet && provider === storedProvider && <span className="text-emerald-600 normal-case font-normal">(tersimpan: {apiKeyMasked})</span>}
          {apiKeySet && provider !== storedProvider && <span className="text-amber-600 normal-case font-normal">⚠ key tersimpan untuk {storedProvider}, bukan {provider}</span>}
        </label>
        <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
          placeholder={apiKeySet && provider === storedProvider
            ? "Kosongkan untuk pertahankan key lama"
            : `Masukkan ${provider === "google" ? "Gemini" : "OpenRouter"} API key`}
          className="w-full rounded-md border px-3 py-2 text-sm font-mono"
          data-testid="llm-api-key-input"/>
        <div className="text-[10px] text-zinc-400 mt-1">
          {provider === "google"
            ? "Dapat di https://aistudio.google.com/app/apikey"
            : "Dapat di https://openrouter.ai/keys"}
        </div>
        {apiKeySet && provider !== storedProvider && (
          <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900">
            <b>Perhatian:</b> provider di-ganti ke <b>{provider}</b> tapi API key yang tersimpan masih untuk <b>{storedProvider}</b>.
            Isi API key <b>{provider}</b> di atas, klik <b>Simpan</b> dulu, baru klik Test Koneksi.
            (Daftar model yang muncul di dropdown bersifat publik — bukan berarti key-nya valid.)
          </div>
        )}
      </div>

      {/* Fetch models button */}
      <div>
        <button onClick={fetchModels} disabled={loadingModels}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-100 hover:bg-zinc-200 text-sm border disabled:opacity-50"
          data-testid="llm-fetch-models-btn">
          {loadingModels ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5"/>}
          Ambil daftar model dari {provider === "google" ? "Google" : "OpenRouter"}
        </button>
        {modelsError && <div className="text-[11px] text-red-600 mt-1">{modelsError}</div>}
        {models.length > 0 && <div className="text-[11px] text-emerald-600 mt-1">{models.length} model tersedia</div>}
      </div>

      {/* Executor model */}
      <div>
        <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
          Executor Model (main AI reply)
        </label>
        {models.length > 0 ? (
          <select value={executorModel} onChange={e => setExecutorModel(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm bg-white"
            data-testid="llm-executor-select">
            <option value="">— pilih —</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        ) : (
          <input value={executorModel} onChange={e => setExecutorModel(e.target.value)}
            placeholder="Ambil daftar dulu, atau ketik manual"
            className="w-full rounded-md border px-3 py-2 text-sm font-mono"
            data-testid="llm-executor-input"/>
        )}
      </div>

      {/* Extractor model */}
      <div>
        <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
          Extractor Model (fast intent parsing)
        </label>
        {models.length > 0 ? (
          <select value={extractorModel} onChange={e => setExtractorModel(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm bg-white"
            data-testid="llm-extractor-select">
            <option value="">— pilih —</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        ) : (
          <input value={extractorModel} onChange={e => setExtractorModel(e.target.value)}
            placeholder="Ambil daftar dulu, atau ketik manual (biasanya model 'lite' / 'flash')"
            className="w-full rounded-md border px-3 py-2 text-sm font-mono"
            data-testid="llm-extractor-input"/>
        )}
      </div>

      {/* v84.164 — AI Admin model (dashboard chat, tool-calling) */}
      <div>
        <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
          AI Admin (dashboard chat, tool-calling)
        </label>
        {models.length > 0 ? (
          <select value={adminModel} onChange={e => setAdminModel(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm bg-white"
            data-testid="llm-admin-select">
            <option value="">— pilih —</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        ) : (
          <input value={adminModel} onChange={e => setAdminModel(e.target.value)}
            placeholder="Kosongkan = pakai Executor. Butuh model tool-calling capable."
            className="w-full rounded-md border px-3 py-2 text-sm font-mono"
            data-testid="llm-admin-input"/>
        )}
        <div className="text-[10px] text-zinc-400 mt-1">
          Dipakai chat AI Admin di dashboard. WAJIB model yg support function-calling
          (Gemini 3.x flash, GPT-4o, Claude 3.5 Sonnet, dll). Kosong = ikut Executor.
        </div>
      </div>

      {/* v85.27 — AI WO Arranger model (dropdown + fallback input) */}
      <div>
        <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
          AI WO Arranger (optimasi susunan WO harian)
        </label>
        {models.length > 0 ? (
          <select value={arrangerModel} onChange={e => setArrangerModel(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm bg-white"
            data-testid="llm-arranger-select">
            <option value="">— pilih —</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        ) : (
          <input value={arrangerModel} onChange={e => setArrangerModel(e.target.value)}
            placeholder="Kosongkan = pakai AI Admin. Direkomendasikan model cepat & murah."
            className="w-full rounded-md border px-3 py-2 text-sm font-mono"
            data-testid="llm-arranger-input"/>
        )}
        <div className="text-[10px] text-zinc-400 mt-1">
          Dipakai agent AI WO Arranger di halaman Settings untuk hasilkan narasi alasan
          penempatan WO. Toggle on/off + system prompt-nya di Settings → AI WO Arranger.
          Kosong = ikut AI Admin.
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2 border-t">
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold disabled:opacity-50"
          data-testid="llm-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
          Simpan
        </button>
        <button onClick={testConn} disabled={testing}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-semibold disabled:opacity-50"
          data-testid="llm-test-btn">
          {testing ? <Loader2 className="w-4 h-4 animate-spin"/> : <Zap className="w-4 h-4"/>}
          Test Koneksi
        </button>
      </div>

      {/* Test result */}
      {testResult && (
        <div className={`rounded-md p-3 text-sm ${testResult.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-800 border border-red-200"}`}
          data-testid="llm-test-result">
          {testResult.ok ? <CheckCircle2 className="w-4 h-4 inline mr-1"/> : <XCircle className="w-4 h-4 inline mr-1"/>}
          {testResult.ok
            ? `Berhasil (${testResult.provider}/${testResult.model}): "${testResult.sample}"`
            : `Gagal: ${testResult.error}`}
        </div>
      )}

      {/* v84.170 — Test Webview WhatsApp */}
      <div className="mt-8 pt-6 border-t-2 border-dashed border-zinc-200" data-testid="webview-test-section">
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle className="w-5 h-5 text-emerald-600" />
          <h2 className="text-lg font-bold">Test Webview WhatsApp</h2>
        </div>
        <p className="text-[12px] text-zinc-500 border-l-2 border-emerald-400 pl-3 py-1 leading-snug mb-4">
          Kirim pesan WhatsApp <b>Interactive CTA URL Button</b> ke nomor tujuan.
          Ketika ditap, tombol akan membuka URL sebagai <b>Webview di dalam WhatsApp</b>
          (tanpa keluar app). Butuh 24h window WA terbuka untuk nomor tersebut.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
              Nomor Tujuan (dengan / tanpa +62)
            </label>
            <input
              type="tel"
              value={wvPhone}
              onChange={e => setWvPhone(e.target.value)}
              placeholder="08123456789"
              className="w-full rounded-md border px-3 py-2 text-sm font-mono"
              data-testid="webview-phone-input"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
              Isi Pesan
            </label>
            <textarea
              rows={3}
              value={wvBody}
              onChange={e => setWvBody(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
              data-testid="webview-body-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
                URL Webview
              </label>
              <input
                type="url"
                value={wvUrl}
                onChange={e => setWvUrl(e.target.value)}
                placeholder="https://..."
                className="w-full rounded-md border px-3 py-2 text-sm font-mono"
                data-testid="webview-url-input"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
                Teks Tombol (max 20)
              </label>
              <input
                type="text"
                value={wvCta}
                maxLength={20}
                onChange={e => setWvCta(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
                data-testid="webview-cta-input"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={sendWebviewTest}
              disabled={wvSending || !wvPhone.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="webview-send-btn"
            >
              {wvSending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
              Kirim Test Webview
            </button>
            <button
              onClick={() => {
                setWvBody(DEFAULT_WEBVIEW_BODY);
                setWvUrl(DEFAULT_WEBVIEW_URL);
                setWvCta("Pilih Jadwal");
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-100 hover:bg-zinc-200 text-sm border"
              data-testid="webview-reset-btn"
            >
              <RefreshCw className="w-3.5 h-3.5"/> Reset default
            </button>
          </div>

          {wvResult && (
            <div
              className={`rounded-md p-3 text-sm ${wvResult.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-800 border border-red-200"}`}
              data-testid="webview-result"
            >
              {wvResult.ok ? <CheckCircle2 className="w-4 h-4 inline mr-1"/> : <XCircle className="w-4 h-4 inline mr-1"/>}
              {wvResult.ok
                ? (wvResult.simulated
                    ? `Simulasi OK → ${wvResult.to}. ${wvResult.message || ""}`
                    : `Terkirim ke ${wvResult.to}${wvResult.wamid ? " · wamid: " + wvResult.wamid : ""}`)
                : `Gagal: ${wvResult.error}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
